import React from 'react'

import { Modal, Button, Form } from '_zari/core'
import { ZariContext } from '_zari/context'

import { IdGen, RounderUtil } from 'utils'

import SeleccionUbicacionModal from '../seleccionUbicacion'

const filaVacia = () => ({
  _idFila: IdGen.newId(),
  numeroLote: '',
  fechaFabricacion: '',
  fechaVencimiento: '',
  cantidad: '',
  _idAlmacen: '',
  _idUbicacion: '',
  _idEstanteria: '',
  codigoUbicacion: '',
  etiquetaUbicacion: '',
  nombreAlmacen: '',
  codigoEstanteria: ''
})

const RegistroLotesModal = ({ show, handleClose, filaProducto, onGuardar }) => {
  const __zariContext = React.useContext(ZariContext)

  const cantidadEsperada = parseFloat(filaProducto?.cantidad || '0')

  const [lotes, setLotes] = React.useState(() => {
    if (filaProducto?.lotes && filaProducto.lotes.length > 0) {
      return filaProducto.lotes.map(l => ({ ...l, _idFila: IdGen.newId() }))
    }
    return [filaVacia()]
  })

  const [filaUbicacionIdx, setFilaUbicacionIdx] = React.useState(null)

  const sumaCantidades = React.useMemo(
    () => lotes.reduce((acc, l) => RounderUtil.round(acc + parseFloat(l.cantidad || '0')), 0),
    [lotes]
  )

  const diferencia = RounderUtil.round(cantidadEsperada - sumaCantidades)

  const agregarFila = () => setLotes(prev => [...prev, filaVacia()])

  const eliminarFila = idFila => {
    setLotes(prev => (prev.length > 1 ? prev.filter(l => l._idFila !== idFila) : prev))
  }

  const actualizarCampo = (idFila, campo, valor) => {
    setLotes(prev => prev.map(l => (l._idFila === idFila ? { ...l, [campo]: valor } : l)))
  }

  const abrirUbicacion = idx => setFilaUbicacionIdx(idx)

  const onUbicacionElegida = ubicacion => {
    if (filaUbicacionIdx == null) return
    setLotes(prev =>
      prev.map((l, i) =>
        i === filaUbicacionIdx
          ? {
              ...l,
              _idAlmacen: ubicacion._idAlmacen,
              _idEstanteria: ubicacion._idEstanteria,
              _idUbicacion: ubicacion._idUbicacion,
              codigoUbicacion: ubicacion.codigoUbicacion,
              etiquetaUbicacion: ubicacion.etiquetaUbicacion,
              nombreAlmacen: ubicacion.nombreAlmacen,
              codigoEstanteria: ubicacion.codigoEstanteria
            }
          : l
      )
    )
    setFilaUbicacionIdx(null)
  }

  const guardar = () => {
    for (let i = 0; i < lotes.length; i++) {
      const l = lotes[i]
      if (!l.numeroLote || !l.numeroLote.trim()) {
        __zariContext.snack('error', `Lote #${i + 1}: ingresa el N° de lote.`)
        return
      }
      if (!l._idUbicacion) {
        __zariContext.snack('error', `Lote #${i + 1}: asigna ubicación.`)
        return
      }
      const cant = parseFloat(l.cantidad || '0')
      if (!cant || cant <= 0) {
        __zariContext.snack('error', `Lote #${i + 1}: cantidad debe ser mayor a cero.`)
        return
      }
    }
    if (diferencia !== 0) {
      __zariContext.snack(
        'error',
        `La suma de cantidades (${sumaCantidades}) no coincide con la cantidad recibida (${cantidadEsperada}).`
      )
      return
    }
    onGuardar(lotes.map(({ _idFila, ...rest }) => rest))
  }

  const filaUbicacionActual = filaUbicacionIdx != null ? lotes[filaUbicacionIdx] : null

  return (
    <>
      <Modal show={show} size='lg' onHide={handleClose}>
        <Modal.Header>
          <Modal.Title>
            <i className='fa fa-boxes mr-2' style={{ color: '#92400E' }} />
            REGISTRAR LOTES
            <small className='ml-2 text-muted'>
              · {filaProducto?.nombreProducto || filaProducto?.codigoProducto} · Cantidad recibida:{' '}
              {cantidadEsperada}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className='mb-3 p-2'
            style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 4, fontSize: '0.78rem' }}
          >
            Distribuye las <b>{cantidadEsperada}</b> unidades en uno o más lotes según lo recibido del proveedor. El{' '}
            <b>N° de lote</b> se captura del empaque del proveedor. Cada lote lleva su propia ubicación física.
          </div>

          <table className='table table-sm table-bordered' style={{ fontSize: '0.78rem' }}>
            <thead style={{ background: '#FEF3C7', color: '#92400E' }}>
              <tr>
                <th style={{ width: 180 }}>N° Lote</th>
                <th style={{ width: 130 }}>Fec. Fab.</th>
                <th style={{ width: 130 }}>Fec. Vencimiento</th>
                <th>Ubicación</th>
                <th style={{ width: 90, textAlign: 'right' }}>Cant.</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {lotes.map((l, idx) => (
                <tr key={l._idFila}>
                  <td>
                    <Form.InputText
                      id={`numeroLote-${l._idFila}`}
                      name={`numeroLote-${l._idFila}`}
                      value={l.numeroLote}
                      onChange={e => actualizarCampo(l._idFila, 'numeroLote', e.target.value)}
                      placeholder='ABC-2025-09'
                    />
                  </td>
                  <td>
                    <Form.InputDate
                      id={`fechaFabricacion-${l._idFila}`}
                      name={`fechaFabricacion-${l._idFila}`}
                      value={l.fechaFabricacion}
                      onChange={e => actualizarCampo(l._idFila, 'fechaFabricacion', e.target.value)}
                    />
                  </td>
                  <td>
                    <Form.InputDate
                      id={`fechaVencimiento-${l._idFila}`}
                      name={`fechaVencimiento-${l._idFila}`}
                      value={l.fechaVencimiento}
                      onChange={e => actualizarCampo(l._idFila, 'fechaVencimiento', e.target.value)}
                    />
                  </td>
                  <td>
                    {l._idUbicacion ? (
                      <div
                        style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                        onClick={() => abrirUbicacion(idx)}
                        title='Click para cambiar'
                      >
                        <strong style={{ color: '#1F3A8A' }}>{l.etiquetaUbicacion}</strong>
                        <span className='text-muted ml-1'>{l.codigoEstanteria}</span>
                        <div className='text-muted'>{l.nombreAlmacen}</div>
                      </div>
                    ) : (
                      <Button size='sm' outline color='primary' onClick={() => abrirUbicacion(idx)}>
                        <i className='fa fa-map-marker-alt mr-1' />
                        Asignar
                      </Button>
                    )}
                  </td>
                  <td>
                    <Form.InputNumber
                      id={`cantidadLote-${l._idFila}`}
                      name={`cantidadLote-${l._idFila}`}
                      value={l.cantidad}
                      onChange={e => actualizarCampo(l._idFila, 'cantidad', e.target.value)}
                      placeholder='0'
                      className='text-right'
                    />
                  </td>
                  <td className='text-center'>
                    <button
                      type='button'
                      className='btn btn-sm btn-link text-danger p-0'
                      onClick={() => eliminarFila(l._idFila)}
                      disabled={lotes.length === 1}
                      title='Eliminar lote'
                    >
                      <i className='fa fa-times' />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Button size='sm' outline color='secondary' onClick={agregarFila}>
            <i className='fa fa-plus mr-1' />
            Agregar otro lote
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <div style={{ marginRight: 'auto', fontSize: '0.85rem' }}>
            <span style={{ color: diferencia === 0 ? '#059669' : '#D97706', fontWeight: 600 }}>
              <span
                className='d-inline-block mr-1'
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: diferencia === 0 ? '#059669' : '#D97706'
                }}
              />
              Suma: {sumaCantidades} / {cantidadEsperada}
              {diferencia === 0 ? ' · OK' : ` · diferencia ${diferencia}`}
            </span>
          </div>
          <Button color='secondary' onClick={handleClose}>
            Cancelar
          </Button>
          <Button color='primary' onClick={guardar} disabled={diferencia !== 0}>
            Guardar lotes
          </Button>
        </Modal.Footer>
      </Modal>

      {filaUbicacionActual && (
        <SeleccionUbicacionModal
          show={true}
          handleClose={() => setFilaUbicacionIdx(null)}
          onSeleccionado={onUbicacionElegida}
          valorActual={
            filaUbicacionActual._idUbicacion
              ? {
                  _idAlmacen: filaUbicacionActual._idAlmacen,
                  _idEstanteria: filaUbicacionActual._idEstanteria,
                  _idUbicacion: filaUbicacionActual._idUbicacion
                }
              : null
          }
          nombreProducto={`${filaProducto?.nombreProducto || ''} · Lote ${filaUbicacionActual.numeroLote || ''}`}
          cantidad={filaUbicacionActual.cantidad}
        />
      )}
    </>
  )
}

export default RegistroLotesModal
